import React, { useState } from 'react';
import { 
  Search, 
  Mail, 
  ChevronDown, 
  ChevronUp,
  HelpCircle,
  Settings,
  Shield,
  Palette,
  Package,
  Users,
  Bell,
  Shirt,
  DollarSign,
  CreditCard,
  Award,
  Box,
  ExternalLink,
  AlertTriangle
} from 'lucide-react';
import ContactSupportModal from '../components/ContactSupportModal';

const Help = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [activeCategory, setActiveCategory] = useState(0);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);

  const faqCategories = [
    {
      title: 'Getting Started',
      icon: HelpCircle,
      faqs: [
        {
          question: 'How do I create my first piece?',
          answer: 'To create your first piece, navigate to the "Create" page from the sidebar. Upload your digital fashion item (image or 3D model), add details like name, description, attributes, and set the quantity for editions. You can save as a draft or mint directly to the Solana blockchain. All prices are shown in SOL with USD conversion.'
        },
        {
          question: 'What file formats are supported?',
          answer: 'We support image formats (PNG, JPG, GIF, WebP) and 3D model format (GLB only) for digital fashion pieces. Maximum file size is 10MB for images and 50MB for 3D models. For best results, optimize your GLB files before uploading. All files are stored securely in our Supabase storage.'
        },
        {
          question: 'How do I connect my wallet?',
          answer: 'Click the "Connect Wallet" button in the sidebar. We support popular Solana wallets like Phantom, Solflare, and Backpack. Make sure you have SOL for transaction fees. Your wallet connection is secure - we never store your private keys.'
        },
        {
          question: 'What are pieces vs NFTs?',
          answer: 'On Outsera, we call digital fashion items "pieces" instead of NFTs. Pieces can be unique 1-of-1 items or limited editions with multiple copies. Each piece is minted on the Solana blockchain as a verified digital asset.'
        }
      ]
    },
    {
      title: 'Capsules & Editions',
      icon: Package,
      faqs: [
        {
          question: 'What are capsules?',
          answer: 'Capsules are mystery boxes that contain random pieces. Creators can mint capsules with a selection of pieces inside. When you open a capsule, you receive a random piece from the capsule\'s collection. It\'s a fun way to collect digital fashion with an element of surprise!'
        },
        {
          question: 'How do I create a capsule?',
          answer: 'Navigate to the "Create Collection" page from the sidebar. Select which pieces to include in your capsule, add a name and description, upload a banner image, and publish it. Each capsule can contain multiple pieces for collectors to discover.'
        },
        {
          question: 'What are editions?',
          answer: 'Editions allow you to create multiple copies of the same piece. When creating a piece, set the quantity to create a limited edition. Each edition is numbered (e.g., #1/10, #2/10) and tracked individually on the blockchain with its own holder.'
        },
        {
          question: 'How do I see who holds editions?',
          answer: 'On any piece detail page with editions, you can see the "Edition Holders" section showing all current holders of each edition number. This provides full transparency on the distribution of your limited edition pieces.'
        },
        {
          question: 'Can I see all my editions?',
          answer: 'Yes! In your Portfolio page, you can view all pieces you own, including which edition numbers you hold. Each edition you own is displayed with its unique number and holder information.'
        }
      ]
    },
    {
      title: 'Marketplace & Listings',
      icon: CreditCard,
      faqs: [
        {
          question: 'How do I list a piece for sale?',
          answer: 'Go to your piece detail page and click "List for Sale". Set your price in SOL (USD equivalent shown), authorize the marketplace delegate if needed, and confirm. Your piece will appear in the marketplace immediately with real-time SOL/USD pricing.'
        },
        {
          question: 'What is the delegate system?',
          answer: 'The delegate system allows the marketplace to transfer your piece on your behalf when sold. You authorize this once per piece, then it can be sold instantly without additional approvals. This makes buying smoother for collectors.'
        },
        {
          question: 'What are the marketplace fees?',
          answer: 'Outsera charges a 2.5% marketplace fee on all sales. Creators also receive royalties (typically 5-10%) on secondary sales. Solana network fees apply for transactions. All fees are shown before confirming.'
        },
        {
          question: 'How do royalties work?',
          answer: 'When you create a piece, you can set a royalty percentage (in basis points, where 100 = 1%). This royalty is automatically paid to you on every resale of your piece, providing ongoing income from your creations.'
        },
        {
          question: 'Can I cancel a listing?',
          answer: 'Yes! Go to your listed piece and click "Cancel Listing". This removes it from the marketplace immediately. You can relist at any time with a different price.'
        },
        {
          question: 'How are prices displayed?',
          answer: 'All prices are shown in SOL with real-time USD conversion. The exchange rate updates automatically so you always know the exact value in both currencies before making any transaction.'
        },
        {
          question: 'Can I see the sale history of a piece?',
          answer: 'Yes! Every piece detail page shows a complete sale history including all past transactions, sale prices, buyer/seller information, and timestamps. This provides full transparency and helps you understand a piece\'s market value and trading history on the blockchain.'
        },
        {
          question: 'How do I track my sales and purchases?',
          answer: 'The Activity page shows real-time updates of all marketplace actions including your sales and purchases. You can view detailed transaction history with links to the Solana blockchain explorer for on-chain verification of every transaction.'
        }
      ]
    },
    {
      title: 'Offers & P2P Trading',
      icon: Users,
      faqs: [
        {
          question: 'How do I make an offer on a piece?',
          answer: 'On any piece detail page, click "Make Offer". Enter your bid amount in SOL (USD shown), add an optional message, and confirm. The owner will receive a notification and can accept or decline your offer.'
        },
        {
          question: 'How do I accept an offer?',
          answer: 'When you receive an offer, you\'ll see it on the Offers page or in your notifications. Click "Accept Offer" to instantly complete the trade. The piece and payment are exchanged atomically on-chain.'
        },
        {
          question: 'What is P2P trading?',
          answer: 'Peer-to-peer (P2P) trading lets you directly trade pieces with other users without listing on the marketplace. Propose a trade with multiple pieces on each side, and when both parties accept, the trade executes automatically on-chain.'
        },
        {
          question: 'How do I propose a P2P trade?',
          answer: 'Go to a piece detail page and click "Request Trade" button. Select pieces from your portfolio to offer in exchange, add an optional message, and submit. The owner will be notified and can accept or decline your trade request. You can also use the Trade Pieces page to manage all your trades.'
        },
        {
          question: 'Can I cancel an offer or trade?',
          answer: 'Yes! You can cancel any offer or trade proposal you\'ve made before it\'s accepted. Go to the Offers page or Trade Pieces page to view and cancel pending offers and trades.'
        },
        {
          question: 'What happens if an offer expires?',
          answer: 'Offers remain active until accepted, declined, or manually cancelled. You can set expiration times on offers if you want them to automatically expire after a certain period.'
        }
      ]
    },
    {
      title: 'Activity & Notifications',
      icon: Bell,
      faqs: [
        {
          question: 'What is the Activity page?',
          answer: 'The Activity page shows real-time updates on all marketplace actions - sales, listings, offers, trades, and more. It\'s updated live as events happen across the platform, so you can see the latest activity instantly.'
        },
        {
          question: 'How do I view my notifications?',
          answer: 'Click the bell icon in the sidebar to view all your notifications. You\'ll receive notifications for piece sales, new offers, trade requests, and other important marketplace activity. All notifications are displayed in real-time.'
        },
        {
          question: 'What notifications will I receive?',
          answer: 'You\'ll receive notifications for: piece sales, new offers on your pieces, accepted offers, completed trades, new followers, favorites on your pieces, new editions minted, and more. All notifications respect your preferences.'
        },
        {
          question: 'How do I view my notification history?',
          answer: 'Click the bell icon in the sidebar to view all your notifications. You can mark them as read, filter by type, and click through to view the related piece or action.'
        },
        {
          question: 'Can I track views on my pieces?',
          answer: 'Yes! Every piece tracks view counts. You can see how many times your pieces have been viewed on their detail pages. This helps you understand which pieces are getting the most attention.'
        }
      ]
    },
    {
      title: 'Creator Verification',
      icon: Award,
      faqs: [
        {
          question: 'How do I get verified on Outsera?',
          answer: 'There are two ways to get verified: (1) Automatic verification - reach 500 sales and you\'ll be automatically verified with a verified badge. (2) Manual verification - apply through the Verification page if you\'re an established creator, influencer, or have a significant following. Manual applications are reviewed by our team.'
        },
        {
          question: 'What are the benefits of being verified?',
          answer: 'Verified creators get a checkmark badge displayed on their profile and all their pieces. This increases trust and credibility with collectors, helps you stand out in the marketplace, and signals that you\'re an established creator on the platform.'
        },
        {
          question: 'How does automatic verification work?',
          answer: 'Once you reach 500 total sales on Outsera, you\'ll be automatically verified. Your sales count tracks every piece sold, and you can see your progress toward verification on the Verification page. When you hit the milestone, your verified badge appears immediately everywhere on the platform.'
        },
        {
          question: 'How do I track my progress toward verification?',
          answer: 'Go to the Verification page to see your current sales count and a progress bar showing how close you are to the 500-sale milestone for automatic verification. This updates in real-time as you make sales.'
        },
        {
          question: 'Can I apply for manual verification before 500 sales?',
          answer: 'Yes! If you\'re an established creator, influencer, or have a significant following on social media, you can request manual verification. Go to the Verification page, fill out the application form with your social links and credentials, and our team will review your request.'
        },
        {
          question: 'What information do I need for manual verification?',
          answer: 'For manual verification, provide your full name, email, social media links (Twitter, Instagram, website), portfolio URL, and explain why you should be verified (minimum 50 characters). Include information about your following, previous work, or other credentials that demonstrate you\'re an established creator.'
        }
      ]
    },
    {
      title: 'Outfit Builder & Portfolio',
      icon: Shirt,
      faqs: [
        {
          question: 'What is the 3D Outfit Builder?',
          answer: 'The 3D Outfit Builder lets you create and customize a digital avatar using your owned pieces. Mix and match clothing items, accessories, and fashion pieces to create unique looks. Your avatar displays in 3D with real-time rendering.'
        },
        {
          question: 'How do I use pieces in the Outfit Builder?',
          answer: 'In the Outfit Builder, select pieces from your portfolio to add to your avatar. You can position, rotate, and scale each piece to fit perfectly. Save multiple outfits and switch between them anytime.'
        },
        {
          question: 'What is the Portfolio page?',
          answer: 'Your Portfolio shows all pieces you own, including individual editions. Browse your collection, see edition numbers, view piece details, and use pieces in the Outfit Builder or list them for sale.'
        },
        {
          question: 'Can I use pieces I don\'t own?',
          answer: 'Only pieces you own (verified on-chain) can be used in your avatar\'s outfit builder. This ensures that your digital fashion is truly yours and verified on the blockchain.'
        },
        {
          question: 'How do I save my avatar outfits?',
          answer: 'When you create an outfit in the Outfit Builder, click "Save Outfit" to store it. You can create multiple outfits and switch between them. Your outfits are saved to your account and load instantly.'
        },
        {
          question: 'Where can I view all my saved outfits?',
          answer: 'Navigate to the Outfits page from the sidebar to see all your saved outfits. You can browse them, edit existing outfits, create new ones, delete old ones, and switch between different looks. Each outfit shows which pieces are included and when it was created.'
        },
        {
          question: 'Can I download my outfits?',
          answer: 'Yes! You can download your outfits as 3D GLB files directly from the Outfits page. Simply click the download button on any saved outfit to export it. This allows you to use your outfits in other 3D applications or share them with others.'
        }
      ]
    },
    {
      title: 'Wallet Warnings & Safety',
      icon: AlertTriangle,
      faqs: [
        {
          question: 'Why does Phantom show a security warning when I buy or trade?',
          answer: 'Phantom may show a security warning when you\'re buying pieces or accepting trades. This is completely normal and actually a GOOD thing! It means Phantom is protecting you by flagging complex transactions. Here\'s what\'s happening and why it\'s safe: When you see the warning, Phantom is alerting you that the transaction involves either (1) granting delegate permissions to transfer NFTs on your behalf, or (2) a multi-signer transaction where both you and our platform sign the same transaction. These are legitimate, necessary operations for NFT marketplaces to function smoothly.'
        },
        {
          question: 'Is it safe to approve transactions with warnings on Outsera?',
          answer: 'Yes, absolutely! Outsera is a legitimate NFT marketplace built on audited smart contracts. Every transaction is transparent and verifiable on the Solana blockchain - you can check any transaction signature in the Solana Explorer to see exactly what happened. The warnings you see are Phantom\'s way of making you aware of complex operations, not an indication that something is wrong. Major NFT marketplaces like Magic Eden, Tensor, and OpenSea all trigger similar warnings in wallet apps. It\'s industry standard for NFT transactions.'
        },
        {
          question: 'What are "delegate approvals" and why do I need them?',
          answer: 'Delegate approvals allow the marketplace to transfer your NFT on your behalf when you accept a trade or list a piece for sale. Instead of requiring you to sign a transaction every time someone wants to buy your piece, you approve the marketplace as a "delegate" once, and then sales can happen instantly. This creates a smoother experience for buyers and sellers. You can revoke delegate access at any time, and delegates can ONLY transfer the specific NFT you approved - they cannot access your other assets or SOL.'
        },
        {
          question: 'What makes multi-signer transactions trigger warnings?',
          answer: 'Multi-signer transactions involve multiple parties signing the same transaction. For example, when you buy an NFT, our platform pre-signs the transaction to transfer the NFT from the seller, then you sign to authorize payment and receive the NFT. Phantom flags these as "complex" because they involve coordination between parties. This is the standard way NFT marketplaces work on Solana - it ensures atomic swaps where both the NFT and payment transfer together or not at all, preventing either party from being scammed.'
        },
        {
          question: 'How can I verify Outsera is legitimate?',
          answer: 'There are several ways to verify Outsera\'s legitimacy: (1) Check transaction history - Every transaction is recorded on the Solana blockchain with public signatures you can verify in Solana Explorer. (2) View our smart contracts - Our marketplace contracts are deployed on-chain and can be audited by anyone. (3) Test with small amounts - Start with inexpensive pieces to build trust before making larger purchases. (4) Community verification - Check our social media and community feedback from other users. (5) Transparent fees - All marketplace fees (2.5%) and royalties are clearly displayed before each transaction.'
        },
        {
          question: 'Do all wallets show these warnings?',
          answer: 'Different wallet providers have different security policies. Phantom is particularly cautious and shows warnings for delegate approvals and multi-signer transactions as a safety feature. Other wallets like Solflare or Backpack may show different warnings or handle these transactions differently. The important thing is understanding what you\'re approving - whether your wallet shows a warning or not, you should always review transaction details before signing.'
        },
        {
          question: 'What should I watch out for when approving transactions?',
          answer: 'Always verify: (1) You\'re on the correct website (outsera.io - check the URL carefully). (2) The transaction amount matches what you expect to pay. (3) You recognize the NFT you\'re buying or trading. (4) The wallet address receiving payment is correct. Never approve transactions if: (1) You didn\'t initiate the action. (2) The amounts seem wrong or too high. (3) You\'re being pressured to act quickly. (4) The website URL looks suspicious or misspelled. When in doubt, cancel the transaction and reach out to support!'
        }
      ]
    },
    {
      title: 'Security & Privacy',
      icon: Shield,
      faqs: [
        {
          question: 'How secure is my wallet connection?',
          answer: 'We never store your private keys. All wallet interactions happen directly through your wallet provider (Phantom, Solflare, etc.). We only request permission to view your public address and sign transactions you explicitly approve.'
        },
        {
          question: 'What is wallet verification?',
          answer: 'Wallet verification proves you own a wallet address by signing a message. This prevents impersonation and ensures only the true wallet owner can perform actions. Verification happens automatically when you connect.'
        },
        {
          question: 'Can I use multiple wallets?',
          answer: 'Yes! You can connect different Solana wallets to use on Outsera. Each wallet has its own collection of pieces and profile. Simply disconnect your current wallet and connect a different one to switch between wallets.'
        },
        {
          question: 'What if I lose access to my wallet?',
          answer: 'We cannot recover access to your wallet as we don\'t store private keys. Always keep your seed phrase safe and secure. Consider using multiple wallets for backup and never share your seed phrase with anyone.'
        },
        {
          question: 'Are my pieces safe on the blockchain?',
          answer: 'Yes! All pieces are minted as Solana NFTs with true ownership on-chain. Even if Outsera went offline, your pieces would still exist on the blockchain and be accessible through other Solana tools.'
        },
        {
          question: 'How is my data protected?',
          answer: 'We use industry-standard encryption for all data. Piece metadata is stored on IPFS for decentralization. Your profile data is stored securely in Supabase with row-level security policies ensuring only you can access your private information.'
        }
      ]
    },
    {
      title: 'Account & Settings',
      icon: Settings,
      faqs: [
        {
          question: 'How do I change my profile information?',
          answer: 'Navigate to your profile page and click the edit button to update your display name, bio, profile picture, banner image, and social links. Changes are saved and appear immediately on your profile.'
        },
        {
          question: 'How do I see my creator stats?',
          answer: 'Your profile shows comprehensive creator statistics including total pieces created, total sales volume, number of collectors, edition counts, and average price. These update in real-time. You can view your verification progress and sales count on the Verification page.'
        },
        {
          question: 'Can I manage favorites?',
          answer: 'Yes! You can favorite any piece by clicking the heart icon. View all your favorites on the Favorites page. This helps you keep track of pieces you love and might want to purchase later.'
        },
        {
          question: 'How do I view my transaction history?',
          answer: 'Go to the Activity page to see your complete transaction history including purchases, sales, offers made/received, and P2P trades. Each transaction links to the Solana blockchain explorer for verification.'
        },
        {
          question: 'How do I manage my profile?',
          answer: 'Navigate to your profile page to view and manage your profile information. You can update your display name, bio, profile picture, banner image, and social links. Your profile showcases all your created and owned pieces.'
        }
      ]
    },
    {
      title: 'Appearance & Customization',
      icon: Palette,
      faqs: [
        {
          question: 'Can I change the theme?',
          answer: 'The platform uses a dark theme optimized for viewing digital fashion and 3D content. This provides the best contrast and visual experience for showcasing pieces.'
        },
        {
          question: 'How do I customize my profile appearance?',
          answer: 'On your profile page, you can upload a profile picture and banner image to personalize your creator page. Choose images that represent your brand and style as a digital fashion creator.'
        },
        {
          question: 'Can I rearrange my piece galleries?',
          answer: 'Pieces are automatically organized by creation date, listing status, and other filters. Use the sorting and filtering options on profile pages to view pieces by price, date, popularity, or other criteria.'
        }
      ]
    },
    {
      title: 'Pricing & Payments',
      icon: DollarSign,
      faqs: [
        {
          question: 'What currency is used on Outsera?',
          answer: 'All transactions use SOL (Solana\'s native cryptocurrency). Prices are displayed in both SOL and USD with real-time conversion rates updated automatically.'
        },
        {
          question: 'How much SOL do I need to get started?',
          answer: 'You\'ll need a small amount of SOL for transaction fees (typically 0.001-0.01 SOL per transaction). For buying pieces, you\'ll need SOL equal to the piece price plus fees.'
        },
        {
          question: 'What are the transaction fees?',
          answer: 'Solana blockchain fees are typically 0.000005 SOL per transaction. Outsera charges a 2.5% marketplace fee on sales. Creator royalties (typically 5-10%) are paid on secondary sales.'
        },
        {
          question: 'When do I get paid for sales?',
          answer: 'Payment is instant! When your piece sells, SOL is transferred directly to your wallet immediately. No waiting periods or withdrawals needed.'
        },
        {
          question: 'How do I set prices for my pieces?',
          answer: 'When listing a piece, enter your desired price in SOL. The USD equivalent is shown automatically. You can update prices anytime by canceling and relisting.'
        }
      ]
    }
  ];

  const filteredFaqs = searchTerm 
    ? faqCategories.map(category => ({
    ...category,
    faqs: category.faqs.filter(faq => 
      faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchTerm.toLowerCase())
    )
      })).filter(category => category.faqs.length > 0)
    : [faqCategories[activeCategory]];

  const toggleFaq = (faqIndex) => {
    setExpandedFaq(expandedFaq === faqIndex ? null : faqIndex);
  };

  return (
    <div className="min-h-screen bg-black">
      {/* Header */}
      <div className="max-w-[1400px] mx-auto px-8 py-12">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-white mb-3">Help & Support</h1>
          <p className="text-gray-400 text-lg">Find answers to your questions about Outsera</p>
        </div>
      </div>

      {/* Search */}
      <div className="px-8 pb-8 bg-black">
        <div className="max-w-3xl mx-auto">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={22} />
            <input
              type="text"
              placeholder="Search for help articles..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full border border-[#2a2a2a] rounded-xl pl-12 pr-4 py-4 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-white focus:border-transparent transition-all duration-200 text-base"
              style={{ backgroundColor: '#111111' }}
            />
          </div>
        </div>
      </div>

      {/* Category Tabs */}
      {!searchTerm && (
        <div className="px-8 py-8 border-y border-[#2a2a2a] bg-black/50">
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-wrap justify-center gap-3 gap-y-3">
              {faqCategories.map((category, index) => {
                const Icon = category.icon;
                const isActive = activeCategory === index;
                return (
                  <button
                    key={category.title}
                    onClick={() => {
                      setActiveCategory(index);
                      setExpandedFaq(null);
                    }}
                    className={`flex items-center gap-2.5 px-5 py-3 rounded-xl font-medium transition-all duration-200 whitespace-nowrap ${
                      isActive
                        ? 'bg-[#2a2a2a] text-white border border-[#3a3a3a]'
                        : 'bg-[#111111] text-gray-400 hover:bg-[#1a1a1a] hover:text-white border border-[#2a2a2a]'
                    }`}
                  >
                    <Icon size={19} />
                    <span>{category.title}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="max-w-[1400px] mx-auto px-8 py-8">
        <div className="grid grid-cols-1 gap-8">
          {/* FAQ Section */}
          <div>
            <div 
              className="rounded-2xl border border-[#2a2a2a] p-6 shadow-xl"
              style={{ backgroundColor: '#111111' }}
            >
              <div className="mb-8">
                {searchTerm ? (
                  <>
                    <h2 className="text-2xl font-bold text-white">
                      Search Results
                      <span className="text-gray-400 text-lg font-normal ml-2">
                      ({filteredFaqs.reduce((total, category) => total + category.faqs.length, 0)} results)
                    </span>
                    </h2>
                    <p className="text-gray-400 text-sm mt-2">Showing results for "{searchTerm}"</p>
                  </>
                ) : (
                  <>
                    <h2 className="text-2xl font-bold text-white">
                      {faqCategories[activeCategory].title}
                    </h2>
                    <p className="text-gray-400 text-sm mt-2">{faqCategories[activeCategory].faqs.length} frequently asked questions</p>
                  </>
                )}
              </div>

              {filteredFaqs.length === 0 ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <HelpCircle size={32} className="text-white" />
                  </div>
                  <p className="text-gray-400 font-medium">No results found for "{searchTerm}"</p>
                  <p className="text-gray-500 text-sm mt-2">Try searching with different keywords</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredFaqs.map((category, categoryIndex) => (
                    <div key={categoryIndex} className="space-y-3">
                      {searchTerm && (
                        <div className="flex items-center gap-3 mb-4 pt-6 first:pt-0 border-t border-[#2a2a2a] first:border-t-0">
                          <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                            {React.createElement(category.icon, { size: 16, className: "text-white" })}
                          </div>
                          <h3 className="text-lg font-semibold text-white">{category.title}</h3>
                        </div>
                      )}
                          {category.faqs.map((faq, faqIndex) => {
                            const globalIndex = categoryIndex * 100 + faqIndex;
                            const isExpanded = expandedFaq === globalIndex;
                            return (
                              <div key={faqIndex} className="border border-[#2a2a2a] rounded-xl bg-[#0a0a0a] hover:border-white transition-all duration-200">
                                <button
                                  onClick={() => toggleFaq(globalIndex)}
                              className="w-full flex items-center justify-between p-5 text-left hover:bg-[#111111] transition-all duration-200"
                                >
                              <span className="font-medium text-white text-base">{faq.question}</span>
                                  {isExpanded ? (
                                <ChevronUp size={18} className="text-white flex-shrink-0 ml-4" />
                                  ) : (
                                <ChevronDown size={18} className="text-gray-400 flex-shrink-0 ml-4" />
                                  )}
                                </button>
                                {isExpanded && (
                              <div className="px-5 pb-5">
                                <p className="text-gray-300 text-base leading-relaxed">{faq.answer}</p>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* 3D Model Creation Tools */}
        <div className="mt-8">
          <div 
            className="rounded-2xl border border-[#2a2a2a] p-8 shadow-xl"
            style={{ backgroundColor: '#111111' }}
          >
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Box size={28} className="text-white" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">3D Model Creation Tools</h2>
              <p className="text-gray-400 text-base max-w-2xl mx-auto">
                Need help creating 3D models for your digital fashion pieces? Check out these AI-powered tools.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Meshy AI */}
              <a
                href="https://www.meshy.ai/?utm_source=chatgpt.com"
                target="_blank"
                rel="noopener noreferrer"
                className="group block border border-[#2a2a2a] rounded-xl p-6 bg-[#0a0a0a] hover:border-white transition-all duration-200 hover:scale-105"
              >
                <div className="flex items-start justify-between mb-4">
                  <h3 className="text-xl font-bold text-white group-hover:text-white">Meshy AI</h3>
                  <ExternalLink size={20} className="text-gray-400 group-hover:text-white transition-colors" />
                </div>
                <p className="text-gray-400 text-sm leading-relaxed mb-4">
                  Transform text and images into stunning 3D models in seconds. Supports text-to-3D, image-to-3D, and AI texturing with professional-grade results.
                </p>
                <div className="inline-flex items-center text-white font-medium text-sm">
                  Visit Meshy AI
                  <ExternalLink size={14} className="ml-2" />
                </div>
              </a>

              {/* Sloyd AI */}
              <a
                href="https://www.sloyd.ai/?utm_source=chatgpt.com"
                target="_blank"
                rel="noopener noreferrer"
                className="group block border border-[#2a2a2a] rounded-xl p-6 bg-[#0a0a0a] hover:border-white transition-all duration-200 hover:scale-105"
              >
                <div className="flex items-start justify-between mb-4">
                  <h3 className="text-xl font-bold text-white group-hover:text-white">Sloyd AI</h3>
                  <ExternalLink size={20} className="text-gray-400 group-hover:text-white transition-colors" />
                </div>
                <p className="text-gray-400 text-sm leading-relaxed mb-4">
                  Create customizable 3D assets instantly with AI. Perfect for rapid prototyping and creating unique 3D models with easy-to-use parametric controls.
                </p>
                <div className="inline-flex items-center text-white font-medium text-sm">
                  Visit Sloyd AI
                  <ExternalLink size={14} className="ml-2" />
                </div>
              </a>

              {/* Tripo3D AI */}
              <a
                href="https://www.tripo3d.ai/?utm_source=chatgpt.com"
                target="_blank"
                rel="noopener noreferrer"
                className="group block border border-[#2a2a2a] rounded-xl p-6 bg-[#0a0a0a] hover:border-white transition-all duration-200 hover:scale-105"
              >
                <div className="flex items-start justify-between mb-4">
                  <h3 className="text-xl font-bold text-white group-hover:text-white">Tripo3D AI</h3>
                  <ExternalLink size={20} className="text-gray-400 group-hover:text-white transition-colors" />
                </div>
                <p className="text-gray-400 text-sm leading-relaxed mb-4">
                  Generate high-quality 3D models from images and text prompts. Fast generation times with detailed outputs ready for use in your projects.
                </p>
                <div className="inline-flex items-center text-white font-medium text-sm">
                  Visit Tripo3D AI
                  <ExternalLink size={14} className="ml-2" />
                </div>
              </a>
            </div>
          </div>
        </div>

        {/* What Are Pieces? Educational Section */}
        <div className="mt-8">
          <div 
            className="rounded-2xl border border-[#2a2a2a] p-8 shadow-xl"
            style={{ backgroundColor: '#111111' }}
          >
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Package size={28} className="text-white" />
              </div>
              <h2 className="text-3xl font-bold text-white mb-3">What Are Pieces?</h2>
              <p className="text-gray-400 text-lg max-w-3xl mx-auto">
                Understanding digital ownership, blockchain technology, and how pieces work on Outsera
              </p>
            </div>

            <div className="max-w-4xl mx-auto space-y-8">
              {/* Introduction */}
              <div className="border-l-4 border-white pl-6 py-2">
                <h3 className="text-xl font-bold text-white mb-3">Pieces = Digital Fashion NFTs</h3>
                <p className="text-gray-300 leading-relaxed">
                  On Outsera, we call digital fashion items "pieces" instead of NFTs (Non-Fungible Tokens). While they're technically the same thing—blockchain-verified digital assets—we use "pieces" because it better represents what they truly are: unique fashion items that you actually own. Each piece is a verifiable digital fashion asset minted on the Solana blockchain, giving you true ownership that exists beyond any single platform.
                </p>
              </div>

              {/* How They Work */}
              <div>
                <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-3">
                  <div className="w-8 h-8 bg-white/10 rounded-lg flex items-center justify-center">
                    <Settings size={18} className="text-white" />
                  </div>
                  How Pieces Work
                </h3>
                <div className="bg-[#0a0a0a] border border-[#2a2a2a] rounded-xl p-6 space-y-4">
                  <p className="text-gray-300 leading-relaxed">
                    When a creator mints a piece on Outsera, they're creating a unique token on the Solana blockchain. This token contains metadata about the fashion item—its image or 3D model, attributes, creator information, and a unique identifier. The blockchain acts as a permanent, tamper-proof ledger that records the piece's entire history: who created it, who owns it, and every transaction it's been involved in.
                  </p>
                  <p className="text-gray-300 leading-relaxed">
                    Each piece can be a 1-of-1 unique item or part of a limited edition series. For editions, each copy is numbered (e.g., #1/10, #2/10) and tracked individually on the blockchain. Every edition has its own unique token ID and ownership record, making each one distinct even though they share the same design.
                  </p>
                </div>
              </div>

              {/* Ownership */}
              <div>
                <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-3">
                  <div className="w-8 h-8 bg-white/10 rounded-lg flex items-center justify-center">
                    <Shield size={18} className="text-white" />
                  </div>
                  True Digital Ownership
                </h3>
                <div className="bg-[#0a0a0a] border border-[#2a2a2a] rounded-xl p-6 space-y-4">
                  <p className="text-gray-300 leading-relaxed">
                    When you own a piece, you truly own it—not just on Outsera, but on the entire Solana blockchain. The piece exists in your wallet, which only you control through your private keys. Outsera can't take it away, freeze it, or control it. If Outsera were to disappear tomorrow, your pieces would still exist on the blockchain and be accessible through any Solana-compatible wallet or marketplace.
                  </p>
                  <p className="text-gray-300 leading-relaxed">
                    This is fundamentally different from traditional digital items like in-game cosmetics or digital purchases from other platforms. With those, you're usually buying a license to use something—the company actually owns it and can revoke access anytime. With blockchain-based pieces, you have cryptographically verified ownership that can't be disputed or taken away.
                  </p>
                </div>
              </div>

              {/* Blockchain Technology */}
              <div>
                <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-3">
                  <div className="w-8 h-8 bg-white/10 rounded-lg flex items-center justify-center">
                    <Box size={18} className="text-white" />
                  </div>
                  The Blockchain Behind It
                </h3>
                <div className="bg-[#0a0a0a] border border-[#2a2a2a] rounded-xl p-6 space-y-4">
                  <p className="text-gray-300 leading-relaxed">
                    Outsera uses the Solana blockchain, one of the fastest and most cost-efficient blockchains available. Solana can process thousands of transactions per second with fees typically under $0.01, making it perfect for digital fashion and collectibles. Unlike some other blockchains where transaction fees can cost $50-100, Solana keeps costs minimal while maintaining security and decentralization.
                  </p>
                  <p className="text-gray-300 leading-relaxed">
                    Every piece is minted using Solana's token standard (SPL tokens), which ensures compatibility across the entire Solana ecosystem. Your pieces can be viewed in any Solana wallet, traded on other Solana marketplaces, and used in any Solana-compatible applications—not just Outsera.
                  </p>
                </div>
              </div>

              {/* Verification & Authenticity */}
              <div>
                <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-3">
                  <div className="w-8 h-8 bg-white/10 rounded-lg flex items-center justify-center">
                    <Award size={18} className="text-white" />
                  </div>
                  Verification & Authenticity
                </h3>
                <div className="bg-[#0a0a0a] border border-[#2a2a2a] rounded-xl p-6 space-y-4">
                  <p className="text-gray-300 leading-relaxed">
                    Every piece on Outsera is verifiably authentic. The blockchain permanently records who the original creator is, making it impossible to fake or counterfeit pieces. When you buy a piece, you can see its complete history: when it was created, previous owners, past sale prices, and more—all verified on-chain.
                  </p>
                  <p className="text-gray-300 leading-relaxed">
                    Creators can also set royalties when minting pieces. These royalties (typically 5-10%) are automatically paid to the creator every time their piece is resold on any marketplace that respects Solana royalties. This ensures creators continue to benefit from their work's success even after the initial sale.
                  </p>
                </div>
              </div>

              {/* What You Can Do */}
              <div>
                <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-3">
                  <div className="w-8 h-8 bg-white/10 rounded-lg flex items-center justify-center">
                    <Shirt size={18} className="text-white" />
                  </div>
                  What You Can Do With Your Pieces
                </h3>
                <div className="bg-[#0a0a0a] border border-[#2a2a2a] rounded-xl p-6">
                  <ul className="space-y-3 text-gray-300">
                    <li className="flex items-start gap-3">
                      <div className="w-1.5 h-1.5 bg-white rounded-full mt-2 flex-shrink-0"></div>
                      <span><strong className="text-white">Display Them:</strong> Showcase your pieces in your portfolio and on your profile page for others to see and admire.</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-1.5 h-1.5 bg-white rounded-full mt-2 flex-shrink-0"></div>
                      <span><strong className="text-white">Wear Them:</strong> Use your pieces in the 3D Outfit Builder to create and customize digital avatars with your owned fashion items.</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-1.5 h-1.5 bg-white rounded-full mt-2 flex-shrink-0"></div>
                      <span><strong className="text-white">Trade Them:</strong> List your pieces for sale on the marketplace, accept offers, or propose peer-to-peer trades with other collectors.</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-1.5 h-1.5 bg-white rounded-full mt-2 flex-shrink-0"></div>
                      <span><strong className="text-white">Collect Them:</strong> Build a curated collection of pieces from your favorite creators, limited editions, and rare finds.</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-1.5 h-1.5 bg-white rounded-full mt-2 flex-shrink-0"></div>
                      <span><strong className="text-white">Transfer Them:</strong> Send pieces to other wallets as gifts or move them between your own wallets freely.</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-1.5 h-1.5 bg-white rounded-full mt-2 flex-shrink-0"></div>
                      <span><strong className="text-white">Prove Ownership:</strong> Use your pieces as proof of support for creators, access to exclusive communities, or entry to special events.</span>
                    </li>
                  </ul>
                </div>
              </div>

              {/* Why They Matter */}
              <div>
                <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-3">
                  <div className="w-8 h-8 bg-white/10 rounded-lg flex items-center justify-center">
                    <DollarSign size={18} className="text-white" />
                  </div>
                  Why Pieces Matter for Digital Fashion
                </h3>
                <div className="bg-[#0a0a0a] border border-[#2a2a2a] rounded-xl p-6 space-y-4">
                  <p className="text-gray-300 leading-relaxed">
                    Digital fashion has always existed, but pieces bring true ownership and scarcity to the space. Creators can establish authentic limited editions, collectors can build valuable portfolios, and both parties benefit from transparent, verifiable transactions. The secondary market allows pieces to appreciate in value, creating economic incentives for quality and creativity.
                  </p>
                  <p className="text-gray-300 leading-relaxed">
                    As the metaverse and digital identities grow, owning verified digital fashion becomes increasingly important. Your pieces are portable across platforms, provably scarce, and truly yours—representing a fundamental shift in how we think about digital ownership and fashion in the internet age.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Contact Support */}
        <div className="mt-8">
          <div 
            className="rounded-2xl border border-[#2a2a2a] p-8 text-center shadow-xl"
            style={{ backgroundColor: '#111111' }}
          >
            <div className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Mail size={28} className="text-white" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">Still need help?</h2>
            <p className="text-gray-400 text-base mb-6 max-w-2xl mx-auto">
              Can't find the answer you're looking for? Our support team is here to help.
            </p>
            <button 
              onClick={() => setIsContactModalOpen(true)}
              className="inline-block px-6 py-3 bg-[#2a2a2a] hover:bg-[#333333] text-white font-medium rounded-xl transition-all duration-200 hover:scale-105 border border-[#3a3a3a]"
            >
              Contact Support
            </button>
          </div>
        </div>
      </div>

      {/* Contact Support Modal */}
      <ContactSupportModal 
        isOpen={isContactModalOpen} 
        onClose={() => setIsContactModalOpen(false)} 
      />
    </div>
  );
};

export default Help; 